import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { EventService } from "../../services/eventService";
import { EventValidator } from "../../validators/eventValidator";
import {
	ApiResponse,
	defaultEventFormData,
	EventDetailItem,
	EventFormData,
	EventListItem,
	EventEditFormData,
	UpdateEventRequest,
	OperationStatus,
	UpdateEventDetailLocalizable,
} from "./constants";
import { getErrorMessage } from "../../utils/errorUtils";

interface EventContextState {
    // Core Domain Data
    eventList: EventListItem[];
    eventDetail: EventDetailItem | null;
    eventForm: EventFormData;
	eventEditForm: EventEditFormData | null;
    operationStatus: OperationStatus;

    // Setters
    setEventForm: React.Dispatch<React.SetStateAction<EventFormData>>;
	setEventEditForm: React.Dispatch<React.SetStateAction<EventEditFormData | null>>;
    clearEventFormData: () => void;
    clearEventList: () => void;

    // Event Actions
    getEventList: () => Promise<void>;
    getEventDetails: (id: string) => Promise<void>;
    addEvent: () => Promise<void>;
    updateEvent: () => Promise<void>;
    removeEvent: (id: string) => Promise<void>;   
}

interface EventProviderProps {
    children: React.ReactNode;
}

const EventContext = createContext<EventContextState | undefined>(undefined);

export const useEventContext = () => {
	const context = useContext(EventContext);
	if (!context) {
		throw new Error("useEventContext must be used within an EventContextProvider");
	}
	return context;
};

const getResponseErrorMessage = (response: ApiResponse<unknown>) => {
	const error = response.error;
	if (typeof error === "string") {
		return error;
	}
	return error?.message || "Beklenmedik bir hata oluştu.";
};

export const EventContextProvider: React.FC<EventProviderProps> = ({ children }) => {
	const navigate = useNavigate();
	const [eventList, setEventList] = useState<EventListItem[]>([]);
	const [eventDetail, setEventDetail] = useState<EventDetailItem | null>(null);
	const [eventForm, setEventForm] = useState<EventFormData>(defaultEventFormData);
	const [eventEditForm, setEventEditForm] = useState<EventEditFormData | null>(null);
	const [operationStatus, setOperationStatus] = useState<OperationStatus>({ type: "idle" });

	const handleError = (error: unknown, operation: string) => {
		console.error(error);
		const errMsg = getErrorMessage(error);
		setOperationStatus({ type: "error", operation, message: errMsg });
		message.error(errMsg);
	};

	/* -------------- Clear helpers -------------- */

	const clearEventFormData = useCallback(() => {
		setEventForm(defaultEventFormData);
		setEventDetail(null);
	}, []);

	const clearEventList = useCallback(() => {
		setEventList([]);
	}, []);

	/* -------------- Build Update Request (Only Changed Fields) -------------- */

	const buildUpdateRequest = (current: EventEditFormData, original: EventDetailItem): UpdateEventRequest => {
		const request: UpdateEventRequest = { eventId: current.eventId };

		// Root fields - include only if changed
		if (current.eventType !== original.eventType) {
			request.eventType = current.eventType;
		}
		if (current.projectDateText !== original.projectDateText) {
			request.projectDateText = current.projectDateText;
		}
		if (current.projectDate !== original.projectDate) {
			request.projectDate = current.projectDate;
		}
		if (current.locationText !== original.locationText) {
			request.locationText = current.locationText;
		}
		if (current.isAppliable !== original.isAppliable) {
			request.isAppliable = current.isAppliable;
		}
		if (current.isActive !== original.isActive) {
			request.isActive = current.isActive;
		}

		// Event Preview - only include if nested fields changed
		const changedPreviewLocalizable: any[] = [];
		current.eventPreview.eventPreviewLocalizable.forEach(item => {
			const before = original.eventPreview.eventPreviewLocalizable.find(
				o => o.eventPreviewLocalizableId === item.eventPreviewLocalizableId
			);
			if (before?.header !== item.header || before?.previewText !== item.previewText || before?.categoryTag !== item.categoryTag) {
				const changed: any = {
					eventPreviewLocalizableId: item.eventPreviewLocalizableId,
					culture: item.culture,
				};
				if (before?.header !== item.header) {
					changed.header = item.header;
				}
				if (before?.previewText !== item.previewText) {
					changed.previewText = item.previewText;
				}

				if (before?.categoryTag !== item.categoryTag) {
					changed.categoryTag = item.categoryTag;
				}

				changedPreviewLocalizable.push(changed);
			}
		});

		if (current.eventPreview.displayOrder !== original.eventPreview.displayOrder ||
			current.eventPreview.isFeatured !== original.eventPreview.isFeatured ||
			changedPreviewLocalizable.length > 0) {
			const preview: any = { eventPreviewId: current.eventPreview.eventPreviewId };
			if (current.eventPreview.displayOrder !== original.eventPreview.displayOrder) {
				preview.displayOrder = current.eventPreview.displayOrder;
			}
			if (current.eventPreview.isFeatured !== original.eventPreview.isFeatured) {
				preview.isFeatured = current.eventPreview.isFeatured;
			}
			if (changedPreviewLocalizable.length > 0) {
				preview.eventPreviewLocalizable = changedPreviewLocalizable;
			}
			request.eventPreview = preview;
		}

		// Event Detail - only include if nested fields changed
		const changedDetailLocalizable: UpdateEventDetailLocalizable[] = [];
		current.eventDetail.eventDetailLocalizable.forEach(item => {
			const before = original.eventDetail.eventDetailLocalizable.find(
				o => o.eventDetailLocalizableId === item.eventDetailLocalizableId
			);
			if (before?.header !== item.header ||
				before?.detailText !== item.detailText ||
				before?.applicantText !== item.applicantText ||
				before?.processText !== item.processText ||
				before?.durationText !== item.durationText ||
				before?.customButtonText !== item.customButtonText ||
				before?.customButtonUrl !== item.customButtonUrl) {
				const changed: any = {
					eventDetailLocalizableId: item.eventDetailLocalizableId,
					culture: item.culture,
				};
				if (before?.header !== item.header) changed.header = item.header;
				if (before?.detailText !== item.detailText) changed.detailText = item.detailText;
				if (before?.applicantText !== item.applicantText) changed.applicantText = item.applicantText;
				if (before?.processText !== item.processText) changed.processText = item.processText;
				if (before?.durationText !== item.durationText) changed.durationText = item.durationText;
				if (before?.customButtonText !== item.customButtonText) changed.customButtonText = item.customButtonText;
				if (before?.customButtonUrl !== item.customButtonUrl) changed.customButtonUrl = item.customButtonUrl;
				changedDetailLocalizable.push(changed);
			}
		});

		if (current.eventDetail.hasCustomButton !== original.eventDetail.hasCustomButton ||
			changedDetailLocalizable.length > 0) {
			const detail: any = { eventDetailId: current.eventDetail.eventDetailId };
			if (current.eventDetail.hasCustomButton !== original.eventDetail.hasCustomButton) {
				detail.hasCustomButton = current.eventDetail.hasCustomButton;
			}
			if (changedDetailLocalizable.length > 0) {
				detail.eventDetailLocalizable = changedDetailLocalizable;
			}
			request.eventDetail = detail;
		}

		if (JSON.stringify(current.eventTagIds) !== JSON.stringify(original.eventTagIds)) {
			request.eventTagIds = current.eventTagIds;
		}

		return request;
	};

	const getEventList = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await EventService.getEventList();

			if (response.isSuccessful) {
				setEventList(response.result?.items ?? []);
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			handleError(error, "fetch");
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, []);

	const getEventDetails = useCallback(async (id: string) => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await EventService.getEventDetails(id);

			if (response.isSuccessful) {
				const result = response.result;
				setEventDetail(result);
				setEventEditForm(result);
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			handleError(error, "fetch");
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, []);

	const addEvent = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "add" });

		const validationError = EventValidator.validateAddEvent(eventForm);
		if (validationError) {
			message.info(validationError);
			setOperationStatus({ type: "idle" });
			return;
		}

		try {
			const response = await EventService.addEvent(eventForm);

			if (response.isSuccessful) {
				message.success("Etkinlik başarıyla eklendi.");
				setEventForm(defaultEventFormData);
				navigate("/events");
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			handleError(error, "add");
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [eventForm, navigate]);

	const updateEvent = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "update" });

		if (!eventEditForm || !eventDetail) {
			message.error("Proje bulunamadı.");
			setOperationStatus({ type: "idle" });
			return;
		}
		const updateRequest = buildUpdateRequest(eventEditForm, eventDetail);

		const validationError = EventValidator.validateUpdateEvent(updateRequest);
		if (validationError) {
			message.info(validationError);
			setOperationStatus({ type: "idle" });
			return;
		}

		try {
			const response = await EventService.updateEvent(updateRequest);

			if (response.isSuccessful) {
				message.success("Etkinlik başarıyla güncellendi.");
				clearEventFormData();
				navigate("/events");
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			handleError(error, "update");
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [eventEditForm, eventDetail, navigate, clearEventFormData]);

	const removeEvent = useCallback(async (id: string) => {
		setOperationStatus({ type: "loading", operation: "delete" });

		try {
			const response = await EventService.removeEvent(id);

			if (response.isSuccessful) {
				message.success("Etkinlik başarıyla silindi.");
				await getEventList();
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			handleError(error, "delete");
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, []);

	const contextValue = useMemo<EventContextState>(
		() => ({
			eventList,
			eventDetail,
			eventForm,
			eventEditForm,
			operationStatus,
			setEventForm,
			setEventEditForm,
			clearEventFormData,
			clearEventList,
			getEventList,
			getEventDetails,
			addEvent,
			updateEvent,
			removeEvent,
		}),
		[
			eventList,
			eventDetail,
			eventForm,
			eventEditForm,
			operationStatus,
			clearEventFormData,
			clearEventList,
			getEventList,
			getEventDetails,
			addEvent,
			updateEvent,
			removeEvent,
		]
	);

	return <EventContext.Provider value={contextValue}>{children}</EventContext.Provider>;
};
